import * as idb from "idb";

const MINIMUM_NONCES = 5;

const DB_NAME = "nonce-db";
const DB_VERSION = 1;
const OBJECT_STORE_NAME = "nonce-table";

/**
 * A lock to prevent multiple requests from triggering multiple refreshes.
 */
let IS_REFRESHING = false;

interface IAuthenticationClientDb extends idb.DBSchema {
  [OBJECT_STORE_NAME]: {
    key: string;
    value: INonce;
  };
}

interface INonce {
  nonce: string;
}


class NonceQueue {
  private queue: (() => Promise<any>)[] = [];
  private isProcessing = false;

  enqueue(task: () => Promise<any>): Promise<any> {
      return new Promise((resolve, reject) => {
          const wrappedTask = async () => {
              try {
                  this.isProcessing = true;
                  const result = await task(); // Execute the task
                  resolve(result); // Resolve the task's result
              } catch (error) {
                  reject(error); // Reject with any encountered error
              } finally {
                  this.isProcessing = false;
                  this.processNext(); // Process next task in the queue
              }
          };

          if (this.isProcessing) {
              this.queue.push(wrappedTask);
          } else {
              wrappedTask(); // Execute the task immediately if not processing
          }
      });
  }

  private processNext(): void {
      if (this.queue.length > 0 && !this.isProcessing) {
          const nextTask = this.queue.shift();
          if (nextTask) {
              nextTask();
          }
      }
  }
}

const nonceQueue = new NonceQueue();

abstract class NonceDbService {
  public static clearNonces = () =>
    NonceDbService.transaction().then(transaction => transaction.store.clear());

  public static getNonce = (refreshNonces: (nonce: string) => Promise<string[]>) => {
    const task = () => {
        return NonceDbService.transaction().then((transaction) => {
          let originalNonce: string | undefined;
          return transaction.store
            .openCursor()
            .then((cursor) => {
                if (cursor === null) {
                    throw new Error(
                        "Error getting nonce - could not get a cursor"
                    );
                }
                originalNonce = cursor.value?.nonce;
                cursor.delete();
                return transaction.store.count();
            })
            .then((count) => {
                console.log(
                    `Nonce count: ${count}, Minimum nonces required: ${MINIMUM_NONCES}`
                );
                if (count < MINIMUM_NONCES && !IS_REFRESHING) {
                    console.log(
                        "Refreshing nonces as the count is below minimum"
                    );
                    IS_REFRESHING = true;
                    return transaction.store
                        .openCursor()
                        .then((cursor) => {
                            if (cursor === null) {
                                throw new Error(
                                    "Error getting refresh nonce - could not get a cursor"
                                );
                            }
                            const refreshNonce = cursor.value?.nonce;
                            cursor.delete();
                            return refreshNonces(refreshNonce).then(
                                (newNonces) => {
                                    console.log(`New nonces received`);
                                    return NonceDbService.transaction().then(
                                        (writeTransaction) => {
                                            newNonces.forEach(
                                                (newNonce) => {
                                                    writeTransaction.store.put(
                                                        {
                                                            nonce: newNonce,
                                                        }
                                                    );
                                                }
                                            );
                                            IS_REFRESHING = false;
                                            return originalNonce ?? "";
                                        }
                                    );
                                }
                            );
                        });
                } else {
                    return originalNonce ?? ""; // Return the original nonce if available
                }
            })
            .catch((error) => {
                console.error("Error in getNonce method:", error);
            });
        });
    };

    return nonceQueue.enqueue(task);
  };

  public static saveNonces = (nonces: string[]) =>
    NonceDbService.transaction().then(transaction =>
      nonces.forEach(nonce => transaction.store.add({ nonce }))
    );

  private static openDb = () =>
    idb.openDB<IAuthenticationClientDb>(DB_NAME, DB_VERSION, {
      upgrade: db =>
        db.createObjectStore(OBJECT_STORE_NAME, {
          autoIncrement: true,
          keyPath: "nonce"
        })
    });

  private static transaction = () =>
    NonceDbService.openDb().then(db =>
      db.transaction(OBJECT_STORE_NAME, "readwrite")
    );
}

export default NonceDbService;
